import { cssManager, css, type CSSResult } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

export const tableStyles: CSSResult[] = [
  themeDefaultStyles,
  cssManager.defaultStyles,
  css`
      :host {
        display: block;
        width: 100%;
      }

      dees-tile {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family);
        font-weight: 400;
        font-size: var(--dees-font-control-size);
        cursor: default;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 var(--dees-spacing-lg);
        height: var(--dees-control-height-lg);
      }

      .headingContainer {
        flex: 1;
        display: flex;
        align-items: baseline;
      }

      .heading {
        line-height: 1;
      }

      .heading1 {
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
      }

      .heading2 {
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-muted);
        margin-left: var(--dees-spacing-sm);
      }

      .headingSeparation {
        display: none;
      }

      .headerActions {
        user-select: none;
        display: flex;
        flex-direction: row;
        gap: var(--dees-spacing-sm);
      }

      .headerAction {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: var(--dees-spacing-xs) 10px;
        font-size: var(--dees-font-caption1-size);
        font-weight: var(--dees-font-control-weight);
        color: var(--dees-color-text-muted);
        background: transparent;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-sm);
        cursor: pointer;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .headerAction:hover {
        color: var(--dees-color-text-primary);
        background: var(--dees-color-hover);
        border-color: var(--dees-color-border-strong);
      }

      .headerAction dees-icon {
        width: 14px;
        height: 14px;
      }

      .searchGrid {
        display: grid;
        grid-gap: var(--dees-spacing-lg);
        grid-template-columns: 1fr max-content;
        padding: var(--dees-spacing-lg) var(--dees-spacing-xl);
        background: var(--dees-color-bg-secondary);
        border-bottom: 1px solid var(--dees-color-border-default);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
      }

      @media (max-width: 900px) {
        .searchGrid {
          grid-template-columns: 1fr;
        }
      }

      /* let search mode size to content (no forced width) */

      .searchGrid.hidden {
        height: 0px;
        opacity: 0;
        overflow: hidden;
        padding: 0px var(--dees-spacing-xl);
        border-bottom-width: 0px;
      }

      /* Default mode (Mode B, page sticky): horizontal scroll lives on
         .tableScroll (so wide tables don't get clipped by an ancestor
         overflow:hidden such as dees-tile). Vertical sticky is handled by
         a JS-managed floating header (.floatingHeader, position:fixed),
         which is unaffected by ancestor overflow. */
      .tableScroll {
        position: relative;
        overflow-x: auto;
        overflow-y: visible;
        scrollbar-gutter: stable;
      }
      /* Mode A, internal scroll: opt-in via fixed-height attribute.
         The table scrolls inside its own box and the header sticks via plain CSS sticky. */
      :host([fixed-height]) .tableScroll {
        max-height: var(--table-max-height, 360px);
        overflow: auto;
        scrollbar-gutter: stable both-edges;
      }
      :host([fixed-height]) .tableScroll::-webkit-scrollbar:horizontal {
        height: 0px;
      }

      /* Floating header overlay (Mode B). Position is managed by JS so it
         escapes any ancestor overflow:hidden (position:fixed is not clipped
         by overflow ancestors). */
      .floatingHeader {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        visibility: hidden;
        overflow: hidden;
        pointer-events: none;
      }
      .floatingHeader.active {
        visibility: visible;
      }
      .floatingHeader table {
        margin: 0;
      }
      .floatingHeader th {
        pointer-events: auto;
      }

      table {
        /* allow table to grow wider than container so actions column can stick */
        width: max-content;
        min-width: 100%;
        caption-side: bottom;
        font-size: var(--dees-font-control-size);
        border-collapse: separate;
        border-spacing: 0;
      }

      .noDataSet {
        padding: var(--dees-spacing-3xl) var(--dees-spacing-xl);
        text-align: center;
        color: var(--dees-color-text-muted);
      }

      thead {
        background: var(--dees-color-code-block-bg);
        border-bottom: 1px solid var(--dees-color-border-strong);
      }
      /* th needs its own background so sticky cells paint over scrolled rows
         (browsers don't paint the <thead> box behind a sticky <th>). */
      th {
        background: var(--dees-color-code-block-bg);
      }
      /* Mode A — internal scroll sticky */
      :host([fixed-height]) thead th {
        position: sticky;
        top: 0;
        z-index: 2;
      }
      :host([fixed-height]) thead tr.filtersRow th {
        top: var(--dees-control-height-md); /* matches th height below */
      }

      tbody tr {
        transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
        position: relative;
        user-select: none;
      }

      /* Default horizontal lines (bottom border only) */
      tbody tr {
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      tbody tr:last-child {
        border-bottom: none;
      }

      /* Full horizontal lines when enabled */
      :host([show-horizontal-lines]) tbody tr {
        border-top: 1px solid var(--dees-color-border-default);
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      :host([show-horizontal-lines]) tbody tr:first-child {
        border-top: none;
      }

      :host([show-horizontal-lines]) tbody tr:last-child {
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      tbody tr:hover {
        background: var(--dees-color-row-hover);
      }

      /* Column hover effect for better traceability */
      td {
        position: relative;
      }

      td::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--dees-color-row-hover);
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--dees-transition-fast) var(--dees-ease-standard);
        z-index: -1;
      }

      td:hover::after {
        opacity: 1;
      }

      /* Grid mode - shows both vertical and horizontal lines */
      :host([show-grid]) th {
        border: 1px solid var(--dees-color-border-default);
        border-left: none;
        border-top: none;
      }

      :host([show-grid]) td {
        border: 1px solid var(--dees-color-border-default);
        border-left: none;
        border-top: none;
      }

      :host([show-grid]) tbody tr:first-child td {
        border-top: none;
      }

      /* Remove edge borders that would double with tile frame */
      :host([show-grid]) th:last-child,
      :host([show-grid]) td:last-child {
        border-right: none;
      }
      :host([show-grid]) tbody tr:last-child td {
        border-bottom: none;
      }

      /* Sticky Actions column (right pinned) */
      thead th.actionsCol,
      tbody td.actionsCol {
        position: sticky;
        right: 0;
        background: var(--dees-color-bg-primary);
      }
      thead th.actionsCol { z-index: 3; }
      tbody td.actionsCol {
        z-index: 1;
        box-shadow: -1px 0 0 0 var(--dees-color-border-default);
      }

      tbody tr.selected {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 10%, transparent);
      }

      tbody tr.hasAttachment {
        background: color-mix(in srgb, var(--dees-color-text-success) 10%, transparent);
      }

      th {
        height: var(--dees-control-height-md);
        padding: var(--dees-spacing-sm) var(--dees-spacing-lg);
        text-align: left;
        font-weight: var(--dees-font-control-weight);
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
      }

      th[role='columnheader']:hover {
        color: var(--dees-color-text-primary);
      }

      th .sortArrow {
        display: inline-block;
        margin-left: 6px;
        font-size: 10px;
        line-height: 1;
        opacity: 0.7;
        vertical-align: middle;
      }

      th .sortBadge {
        display: inline-block;
        margin-left: 3px;
        padding: 1px 5px;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
        color: color-mix(in srgb, var(--dees-color-accent-primary) 70%, var(--dees-color-text-primary));
        background: color-mix(in srgb, var(--dees-color-accent-primary) 15%, transparent);
        border-radius: var(--dees-radius-full);
        vertical-align: middle;
      }

      :host([show-vertical-lines]) th {
        border-right: 1px solid var(--dees-color-border-default);
      }

      td {
        padding: var(--dees-spacing-sm) var(--dees-spacing-lg);
        vertical-align: middle;
        font-size: var(--dees-font-control-size-sm);
        color: var(--dees-color-text-primary);
      }

      :host([show-vertical-lines]) td {
        border-right: 1px solid var(--dees-color-border-default);
      }

      th:first-child,
      td:first-child {
        padding-left: var(--dees-spacing-lg);
      }

      th:last-child,
      td:last-child {
        padding-right: var(--dees-spacing-lg);
      }

      :host([show-vertical-lines]) th:last-child,
      :host([show-vertical-lines]) td:last-child {
        border-right: none;
      }

      /* Default bottom border on last row removed — tile frame handles it */
      :host(:not([show-horizontal-lines])) tbody tr:last-child {
        border-bottom: none;
      }

      .innerCellContainer {
        position: relative;
        min-height: 24px;
        line-height: 24px;
      }

      /* ---- Cell flash highlighting (opt-in via highlight-updates="flash") ----
         Bloomberg/TradingView-style: the text itself briefly takes an accent
         color then fades back to the default. No background tint, no layout
         shift, no weight change. Readable, modern, subtle.
         Consumers can override per instance:
           dees-table#myTable { --dees-table-flash-color: hsl(142 76% 40%); }
      */
      :host {
        --dees-table-flash-color: var(--dees-color-accent-warning);
        --dees-table-flash-easing: cubic-bezier(0.22, 0.61, 0.36, 1);
      }

      .innerCellContainer.flashing {
        animation: dees-table-cell-flash
                   var(--dees-table-flash-duration, 900ms)
                   var(--dees-table-flash-easing);
      }

      /* Hold the accent color briefly, then fade back to the theme's default
         text color. Inherits to child text and to SVG icons that use
         currentColor. Cells with explicit color overrides in renderers are
         intentionally unaffected. */
      @keyframes dees-table-cell-flash {
        0%,
        35% { color: var(--dees-table-flash-color); }
        100% { color: var(--dees-color-text-primary); }
      }

      /* Border/background flash variant for cells with styled content
         (badges, icons, custom components) where a text-color animation
         would be invisible. Activated via flashBorder on Column. */
      .innerCellContainer.flashing-border {
        animation: dees-table-cell-flash-border
                   var(--dees-table-flash-duration, 900ms)
                   var(--dees-table-flash-easing);
        border-radius: 3px;
      }

      @keyframes dees-table-cell-flash-border {
        0%,
        35% {
          box-shadow: inset 0 0 0 1.5px var(--dees-table-flash-color);
          background: color-mix(in srgb, var(--dees-color-accent-warning) 10%, transparent);
        }
        100% {
          box-shadow: inset 0 0 0 1.5px transparent;
          background: transparent;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .innerCellContainer.flashing {
          animation: none;
          color: var(--dees-table-flash-color);
        }
        .innerCellContainer.flashing-border {
          animation: none;
          box-shadow: inset 0 0 0 1.5px var(--dees-table-flash-color);
          background: color-mix(in srgb, var(--dees-color-accent-warning) 10%, transparent);
        }
      }

      /* Dev-time warning banner shown when highlight-updates="flash" but
         rowKey is missing. Consumers should never ship this to production. */
      .flashConfigWarning {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        margin: var(--dees-spacing-sm) var(--dees-spacing-lg) 0;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border-left: 3px solid var(--dees-color-accent-warning);
        background: color-mix(in srgb, var(--dees-color-accent-warning) 18%, transparent);
        color: var(--dees-color-badge-warning-fg);
        font-size: var(--dees-font-caption1-size);
        line-height: 1.4;
        border-radius: var(--dees-radius-sm);
      }
      .flashConfigWarning dees-icon {
        width: 14px;
        height: 14px;
        flex: 0 0 auto;
      }
      .flashConfigWarning code {
        padding: 1px 4px;
        border-radius: 3px;
        background: color-mix(in srgb, var(--dees-color-bg-primary) 50%, transparent);
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-caption2-size);
      }

      /* Editable cell affordances */
      td.editable {
        cursor: text;
      }
      td.focused {
        outline: 2px solid color-mix(in srgb, var(--dees-color-accent-primary) 60%, transparent);
        outline-offset: -2px;
      }
      td.editingCell {
        padding: 0;
        outline: 2px solid color-mix(in srgb, var(--dees-color-accent-primary) 60%, transparent);
        outline-offset: -2px;
      }
      td.editingCell .innerCellContainer {
        padding: 0;
        line-height: normal;
      }
      td.editingCell dees-input-text,
      td.editingCell dees-input-checkbox,
      td.editingCell dees-input-dropdown,
      td.editingCell dees-input-datepicker,
      td.editingCell dees-input-tags {
        display: block;
        width: 100%;
      }

      /* filter row */
      thead tr.filtersRow th {
        padding: var(--dees-spacing-sm) var(--dees-spacing-md) var(--dees-spacing-md) var(--dees-spacing-md);
      }
      thead tr.filtersRow th input[type='text'] {
        width: 100%;
        box-sizing: border-box;
        padding: 6px var(--dees-spacing-sm);
        font-size: var(--dees-font-control-size-sm);
        border-radius: var(--dees-radius-sm);
        border: 1px solid var(--dees-color-border-default);
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
      }
      .actionsContainer {
        display: flex;
        flex-direction: row;
        gap: var(--dees-spacing-xs);
      }

      .action {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--dees-control-height-sm);
        height: var(--dees-control-height-sm);
        border-radius: var(--dees-radius-sm);
        color: var(--dees-color-text-muted);
        cursor: pointer;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .action:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .action:active {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      .action dees-icon {
        width: 16px;
        height: 16px;
      }

      .footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-caption2-size);
        color: var(--dees-color-text-muted);
        width: 100%;
        box-sizing: border-box;
      }

      .tableStatistics {
        font-weight: var(--dees-font-control-weight);
      }

      .footerActions {
        display: flex;
        gap: var(--dees-spacing-sm);
      }

      .footerActions .footerAction {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 2px var(--dees-spacing-sm);
        font-weight: var(--dees-font-control-weight);
        font-size: var(--dees-font-caption2-size);
        color: var(--dees-color-text-muted);
        border-radius: var(--dees-radius-sm);
        cursor: pointer;
        user-select: none;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .footerActions .footerAction:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .footerActions .footerAction dees-icon {
        width: 14px;
        height: 14px;
      }
  `,
];
